import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { BadgeCheck, XCircle, ArrowDown, ArrowRight } from "lucide-react";
import { useLenis, scrollToSection } from "@/App";
import { RECORDS, combinedOdds } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DoubleCard = ({ record, index }) => (
    <motion.article
        data-testid={`double-card-${index + 1}`}
        initial={{ opacity: 0, y: 28 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-40px" }}
        transition={{ duration: 0.55, delay: (index % 4) * 0.08 }}
        className="group relative bg-bn-card/70 border border-bn-line rounded-xl p-5 hover:border-bn-blue/50 hover:-translate-y-1.5 hover:shadow-[0_14px_40px_rgba(0,102,255,0.18)] transition-all duration-300"
    >
        <div className="flex items-center justify-between mb-4">
            <span className="font-mono text-xs text-slate-400 tracking-wider">{record.date}</span>
            {record.status === "lost" ? (
                <span data-testid={`double-status-${index + 1}`} className="flex items-center gap-1.5 font-mono text-[10px] font-bold px-2.5 py-1 rounded-full bg-red-400/10 text-red-400 border border-red-400/30">
                    <XCircle className="w-3 h-3" /> LOST
                </span>
            ) : (
                <span data-testid={`double-status-${index + 1}`} className="flex items-center gap-1.5 font-mono text-[10px] font-bold px-2.5 py-1 rounded-full bg-bn-green/10 text-bn-green border border-bn-green/30">
                    <BadgeCheck className="w-3 h-3" /> WON
                </span>
            )}
        </div>
        <div className="space-y-3.5">
            {record.matches.map((m, i) => (
                <div key={i} className="pb-3.5 border-b border-white/5 last:border-0 last:pb-0">
                    <div className="text-sm font-semibold text-slate-100 leading-snug">{m.teams}</div>
                    <div className="mt-2 flex items-center justify-between gap-2">
                        <span className="font-mono text-[11px] font-semibold px-2 py-1 rounded bg-bn-blue/10 text-bn-cyan border border-bn-blue/25">
                            {m.pick} ({m.odds.toFixed(2)})
                        </span>
                        <span className="font-mono text-[11px] text-slate-500">{m.score}</span>
                    </div>
                </div>
            ))}
        </div>
        <div className="mt-4 pt-3.5 border-t border-bn-line flex items-center justify-between">
            <span className="font-mono text-[10px] tracking-[0.2em] text-slate-500">COMBINED ODDS</span>
            <span data-testid={`double-odds-${index + 1}`} className="font-display font-extrabold text-xl text-bn-blue group-hover:text-glow-blue transition-all">
                {combinedOdds(record)}
            </span>
        </div>
    </motion.article>
);

export const Records = () => {
    const lenisRef = useLenis();
    const [records, setRecords] = useState(RECORDS.map((r) => ({ ...r, status: "won" })));
    const [limit, setLimit] = useState(20);
    const [shown, setShown] = useState(4);

    useEffect(() => {
        axios
            .get(`${API}/doubles`)
            .then((res) => {
                if (Array.isArray(res.data) && res.data.length) setRecords(res.data);
            })
            .catch(() => {});
        axios
            .get(`${API}/content`)
            .then((res) => {
                if (res.data?.display_limits?.doubles) setLimit(res.data.display_limits.doubles);
            })
            .catch(() => {});
    }, []);
    return (
        <section id="records" data-testid="records-section" className="relative py-20 lg:py-32 bg-[#0B0F17]/50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7 }}
                    className="text-center mb-14"
                >
                    <div className="font-mono text-[11px] tracking-[0.3em] text-bn-blue font-semibold mb-4">OFFER 2 · DAILY DOUBLES</div>
                    <h2 data-testid="records-heading" className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-white">
                        LATEST WINNING <span className="text-bn-blue text-glow-blue">DOUBLES</span>
                    </h2>
                    <p className="mt-4 text-base text-slate-400 max-w-xl mx-auto">
                        The latest {limit} winning doubles received by our subscribers.
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                    {records.slice(0, shown).map((r, i) => (
                        <DoubleCard key={r.double_id || r.date} record={r} index={i} />
                    ))}
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7 }}
                    className="mt-12 text-center"
                >
                    {shown < Math.min(records.length, limit) ? (
                        <button
                            data-testid="records-view-more-button"
                            onClick={() => setShown((s) => s + 4)}
                            className="group inline-flex items-center gap-2.5 px-8 py-4 rounded-full border border-white/15 hover:border-bn-blue/60 text-slate-200 hover:text-white font-semibold text-sm tracking-wide transition-all duration-300 hover:bg-bn-blue/10"
                        >
                            VIEW MORE
                            <ArrowDown className="w-4 h-4 transition-transform duration-300 group-hover:translate-y-1" />
                        </button>
                    ) : (
                        <button
                            data-testid="records-subscribe-archive-button"
                            onClick={() => scrollToSection(lenisRef.current, "subscription")}
                            className="group inline-flex items-center gap-2.5 px-8 py-4 rounded-full bg-bn-blue hover:bg-bn-bluehover text-white font-semibold text-sm tracking-wide transition-all duration-300 hover:shadow-[0_0_35px_rgba(0,102,255,0.55)]"
                        >
                            SUBSCRIBE AND SEE FULL ARCHIVE
                            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </button>
                    )}
                </motion.div>
            </div>
        </section>
    );
};
